import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, CurrencyDollar } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { money, periodLabel, fdate } from "@/lib/format";
import { PageHeader, EmptyState, StatusBadge, StatCard } from "@/components/common";
import { TextField, SelectField } from "@/components/fields";
import { SingleUpload } from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const currentPeriod = new Date().toISOString().slice(0, 7);

export default function Cobranza() {
  const [items, setItems] = useState([]);
  const [periodo, setPeriodo] = useState("");
  const [estado, setEstado] = useState("todos");
  const [payOpen, setPayOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [pago, setPago] = useState({ monto: "", metodo: "transferencia", nota: "", banco_id: "" });
  const [accounts, setAccounts] = useState([]);
  useEffect(() => { api.get("/accounts").then((r) => setAccounts(r.data)); }, []);

  const load = useCallback(() => {
    const params = {};
    if (periodo) params.periodo = periodo;
    if (estado && estado !== "todos") params.estado = estado;
    api.get("/cobranza", { params }).then((r) => setItems(r.data));
  }, [periodo, estado]);
  useEffect(() => { load(); }, [load]);

  const generar = async () => {
    try {
      const r = await api.post("/cobranza/generar", { periodo: periodo || currentPeriod });
      toast.success(`Cobranza generada: ${r.data.creados} nuevo(s) para ${periodLabel(r.data.periodo)}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openPay = (c) => { setActive(c); setPago({ monto: c.saldo, metodo: "transferencia", nota: "", banco_id: "", comprobante: "" }); setPayOpen(true); };
  const registerPay = async () => {
    if (!pago.monto || pago.monto <= 0) return toast.error("Importe inválido");
    try {
      await api.post(`/cobranza/${active.id}/pago`, { monto: parseFloat(pago.monto), metodo: pago.metodo, nota: pago.nota, banco_id: pago.banco_id || null, comprobante: pago.comprobante || null });
      toast.success("Pago registrado. Se calculó la comisión y el neto del propietario.");
      setPayOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const totalPend = items.filter((i) => ["pendiente", "parcial", "vencido"].includes(i.estado)).reduce((s, i) => s + i.saldo, 0);
  const totalCobrado = items.reduce((s, i) => s + (i.monto_pagado || 0), 0);
  const totalVencido = items.filter((i) => i.estado === "vencido").reduce((s, i) => s + i.saldo, 0);

  const ESTADOS = [{ value: "todos", label: "Todos" }, { value: "pendiente", label: "Pendiente" }, { value: "parcial", label: "Parcial" }, { value: "pagado", label: "Pagado" }, { value: "vencido", label: "Vencido" }, { value: "cancelado", label: "Cancelado" }];

  return (
    <div data-testid="cobranza-page">
      <PageHeader title="Cobranza" subtitle="Rentas generadas, pagos y estados" actions={<Button data-testid="generar-cobranza-btn" onClick={generar} className="rounded-sm gap-2"><ArrowsClockwise size={16} /> Generar cobranza del periodo</Button>} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Cargos" value={items.length} />
        <StatCard label="Cobrado" value={money(totalCobrado)} tone="accent" />
        <StatCard label="Pendiente" value={money(totalPend)} tone="warn" />
        <StatCard label="Vencido" value={money(totalVencido)} tone="danger" />
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="w-40"><TextField label="Periodo (YYYY-MM)" value={periodo} onChange={setPeriodo} testId="filter-periodo" placeholder={currentPeriod} /></div>
        <div className="w-44"><SelectField label="Estado" value={estado} onChange={setEstado} testId="filter-estado" options={ESTADOS} /></div>
      </div>

      {items.length === 0 ? (
        <EmptyState title="Sin cargos de cobranza" hint="Crea contratos vigentes y genera la cobranza del periodo." action={<Button onClick={generar} className="rounded-sm gap-2"><ArrowsClockwise size={16} /> Generar cobranza</Button>} />
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Periodo</TableHead><TableHead>Inmueble</TableHead><TableHead>Arrendatario</TableHead><TableHead>Vence</TableHead><TableHead className="text-right">Importe</TableHead><TableHead className="text-right">Comisión</TableHead><TableHead className="text-right">Neto prop.</TableHead><TableHead className="text-right">Saldo</TableHead><TableHead>Estado</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} data-testid={`charge-row-${c.id}`}>
                  <TableCell>{periodLabel(c.periodo)}</TableCell>
                  <TableCell className="font-medium">{c.property_nombre}</TableCell>
                  <TableCell>{c.tenant_nombre}</TableCell>
                  <TableCell className="text-xs">{fdate(c.fecha_vencimiento)}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(c.importe)}</TableCell>
                  <TableCell className="text-right tabular-nums text-primary">{money(c.comision_importe)}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(c.neto_propietario)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{money(c.saldo)}</TableCell>
                  <TableCell><StatusBadge estado={c.estado} /></TableCell>
                  <TableCell className="text-right">
                    {["pendiente", "parcial", "vencido"].includes(c.estado) && (
                      <Button size="sm" className="rounded-sm gap-1" data-testid={`pay-btn-${c.id}`} onClick={() => openPay(c)}><CurrencyDollar size={14} /> Pagar</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle>Registrar pago</DialogTitle></DialogHeader>
          {active && (
            <div className="text-sm space-y-1 mb-2">
              <p>{active.property_nombre} · {periodLabel(active.periodo)}</p>
              <p className="text-muted-foreground">Saldo pendiente: <span className="font-bold tabular-nums text-foreground">{money(active.saldo)}</span></p>
            </div>
          )}
          <div className="grid gap-3">
            <TextField label="Monto" type="number" value={pago.monto} onChange={(v) => setPago((p) => ({ ...p, monto: v }))} testId="pago-monto" />
            <SelectField label="Método" value={pago.metodo} onChange={(v) => setPago((p) => ({ ...p, metodo: v }))} options={[{ value: "transferencia", label: "Transferencia" }, { value: "efectivo", label: "Efectivo" }, { value: "cheque", label: "Cheque" }, { value: "tarjeta", label: "Tarjeta" }]} />
            <SelectField label="Cuenta de depósito (opcional)" value={pago.banco_id} onChange={(v) => setPago((p) => ({ ...p, banco_id: v }))} testId="pago-banco" options={accounts.map((a) => ({ value: a.id, label: `${a.nombre} (${a.proposito})` }))} />
            <TextField label="Nota" value={pago.nota} onChange={(v) => setPago((p) => ({ ...p, nota: v }))} />
            <SingleUpload value={pago.comprobante} onChange={(v) => setPago((p) => ({ ...p, comprobante: v }))} testId="pago-comprobante" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setPayOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm" data-testid="save-pago-btn" onClick={registerPay}>Registrar pago</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
